import api from '../services/api';
import { 
  preparePeriodForApi, 
  debugDates, 
  normalizeDateFields,
  formatToDateString 
} from '../utils/dateUtils';
import { formatDateSafe } from '../utils/dateFormatter';

/**
 * Service pour interagir avec l'API de disponibilité
 * Implémentation selon PHASE_2_IMPLEMENTATION_PLAN.md
 */

// Utiliser notre instance API directement - remove /api/v1 prefix as it's already added by the main api service
const apiClient = {
  get: async (url, options = {}) => {
    // Remove /api/v1 prefix if present
    const cleanUrl = url.startsWith('/api/v1') ? url.substring(7) : url;
    console.log(`📡 [AvailabilityService] Making request to: ${cleanUrl}`);
    const response = await api.get(cleanUrl, options);
    return response;
  },
  post: async (url, data) => {
    // Remove /api/v1 prefix if present
    const cleanUrl = url.startsWith('/api/v1') ? url.substring(7) : url;
    console.log(`📡 [AvailabilityService] Making POST request to: ${cleanUrl}`);
    const response = await api.post(cleanUrl, data);
    return response;
  },
  put: async (url, data) => {
    // Remove /api/v1 prefix if present
    const cleanUrl = url.startsWith('/api/v1') ? url.substring(7) : url;
    console.log(`📡 [AvailabilityService] Making PUT request to: ${cleanUrl}`);
    const response = await api.put(cleanUrl, data);
    return response;
  },
  delete: async (url) => {
    // Remove /api/v1 prefix if present
    const cleanUrl = url.startsWith('/api/v1') ? url.substring(7) : url;
    console.log(`📡 [AvailabilityService] Making DELETE request to: ${cleanUrl}`);
    const response = await api.delete(cleanUrl);
    return response;
  }
};

/**
 * Récupère les périodes de disponibilité d'un listing
 * @param {string} listingId - ID du listing
 * @param {Object} options - Options de filtrage (startDate, endDate, status)
 * @returns {Promise<Array>} - Liste des périodes de disponibilité
 */
export const fetchAvailability = async (listingId, options = {}) => {
  try {
    console.log(`🔍 [AvailabilityService] Fetching availability for listing: ${listingId}`);
    const { startDate, endDate, status } = options;
    
    let queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', formatDateSafe(startDate));
    if (endDate) queryParams.append('endDate', formatDateSafe(endDate));
    if (status) queryParams.append('status', status);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const url = `/api/v1/listings/${listingId}/availability${queryString}`;
    console.log(`📡 [AvailabilityService] Making request to: ${url}`);
    
    const response = await apiClient.get(url);
    console.log(`📊 [AvailabilityService] Response received:`, response);
    
    if (response.data && response.data.success) {
      console.log(`✅ [AvailabilityService] Found ${response.data.data?.length || 0} availability periods`);
      return response.data.data;
    }
    
    console.log(`⚠️ [AvailabilityService] No data or unsuccessful response`);
    return [];
  } catch (error) {
    console.error('❌ [AvailabilityService] Error fetching availability:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des disponibilités');
  }
};

/**
 * Ajoute une nouvelle période de disponibilité
 * @param {string} listingId - ID du listing
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @param {string} status - Statut de la période
 * @param {string} reason - Raison (optionnel)
 * @returns {Promise<Object>} - Période créée
 */
export const addAvailabilityPeriod = async (listingId, startDate, endDate, status, reason = null) => {
  try {
    console.log('➕ [AvailabilityService] Adding availability period');
    
    // UTILISER L'UTILITAIRE CENTRALISÉ POUR NORMALISER LES DATES
    const periodData = {
      startDate: startDate,
      endDate: endDate,
      status: status,
      reason: reason
    };
    
    debugDates("AddPeriod Frontend Input", { startDate, endDate });
    
    // Préparer les données pour l'API avec formatting centralisé
    const apiData = preparePeriodForApi(periodData);
    
    debugDates("AddPeriod API Data", apiData);
    
    console.log('📡 [AvailabilityService] Making POST request to:', `/listings/${listingId}/availability`);
    console.log('📅 [AvailabilityService] Dates being sent:', apiData);
    
    const response = await apiClient.post(`/api/v1/listings/${listingId}/availability`, apiData);
    
    if (response.data && response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data?.message || 'Erreur lors de l\'ajout de la période');
  } catch (error) {
    console.error('Error adding availability period:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de l\'ajout de la période');
  }
};

/**
 * Met à jour une période de disponibilité
 * @param {number} periodId - ID de la période
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise<Object>} - Période mise à jour
 */
export const updateAvailabilityPeriod = async (periodId, data) => {
  try {
    console.log('🔄 [AvailabilityService] Updating availability period:', periodId);
    
    debugDates("UpdatePeriod Frontend Input", data);
    
    // UTILISER L'UTILITAIRE CENTRALISÉ POUR NORMALISER LES DATES
    const normalizedData = normalizeDateFields(data);
    
    // Préparer les données pour l'API
    const requestData = {};
    
    if (normalizedData.startDate) requestData.available_from = formatToDateString(normalizedData.startDate);
    if (normalizedData.endDate) requestData.available_to = formatToDateString(normalizedData.endDate);
    if (normalizedData.available_from) requestData.available_from = formatToDateString(normalizedData.available_from);
    if (normalizedData.available_to) requestData.available_to = formatToDateString(normalizedData.available_to);
    if (normalizedData.status) requestData.status = normalizedData.status;
    if (normalizedData.reason !== undefined) requestData.reason = normalizedData.reason;
    
    debugDates("UpdatePeriod API Data", requestData);
    
    const response = await apiClient.put(`/api/v1/availability/${periodId}`, requestData);
    
    if (response.data && response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data?.message || 'Erreur lors de la mise à jour de la période');
  } catch (error) {
    console.error('Error updating availability period:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour de la période');
  }
};

/**
 * Supprime une période de disponibilité
 * @param {number} periodId - ID de la période
 * @returns {Promise<boolean>} - Succès de l'opération
 */
export const deleteAvailabilityPeriod = async (periodId) => {
  try {
    const response = await apiClient.delete(`/api/v1/availability/${periodId}`);
    
    if (response.data && response.data.success) {
      return true;
    }
    
    throw new Error(response.data?.message || 'Erreur lors de la suppression de la période');
  } catch (error) {
    console.error('Error deleting availability period:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la suppression de la période');
  }
};

/**
 * Supprime une période de disponibilité via l'endpoint du listing (alternative plus sûre)
 * @param {string} listingId - ID du listing
 * @param {number} periodId - ID de la période
 * @returns {Promise<boolean>} - Succès de l'opération
 */
export const deleteAvailabilityPeriodViaListing = async (listingId, periodId) => {
  try {
    console.log(`🔍 [AvailabilityService] Tentative suppression via listing ${listingId} pour période ${periodId}`);
    const response = await apiClient.delete(`/api/v1/listings/${listingId}/availability/${periodId}`);
    
    if (response.data && response.data.success) {
      console.log(`✅ [AvailabilityService] Période ${periodId} supprimée avec succès via listing`);
      return true;
    }
    
    throw new Error(response.data?.message || 'Erreur lors de la suppression de la période');
  } catch (error) {
    console.error('Error deleting availability period via listing:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la suppression de la période');
  }
};

/**
 * Bloque une période pour un listing
 * @param {string} listingId - ID du listing
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @param {string} reason - Raison du blocage
 * @returns {Promise<Object>} - Période bloquée
 */
export const blockPeriod = async (listingId, startDate, endDate, reason = null) => {
  try {
    const response = await apiClient.post(`/api/v1/listings/${listingId}/block`, {
      startDate: formatDateSafe(startDate),
      endDate: formatDateSafe(endDate),
      reason
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data?.message || 'Erreur lors du blocage de la période');
  } catch (error) {
    console.error('Error blocking period:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors du blocage de la période');
  }
};

/**
 * Libère une période pour un listing
 * @param {string} listingId - ID du listing
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {Promise<Object>} - Période libérée
 */
export const freePeriod = async (listingId, startDate, endDate) => {
  try {
    const response = await apiClient.post(`/api/v1/listings/${listingId}/free`, {
      startDate: formatDateSafe(startDate),
      endDate: formatDateSafe(endDate)
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data?.message || 'Erreur lors de la libération de la période');
  } catch (error) {
    console.error('Error freeing period:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la libération de la période');
  }
};

/**
 * Vérifie les conflits pour une période
 * @param {string} listingId - ID du listing
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @param {number} excludeId - ID de période à exclure (optionnel)
 * @returns {Promise<Array>} - Liste des conflits
 */
export const checkConflicts = async (listingId, startDate, endDate, excludeId = null) => {
  try {
    let queryParams = new URLSearchParams({
      listingId,
      startDate: formatDateSafe(startDate),
      endDate: formatDateSafe(endDate)
    });
    
    if (excludeId) queryParams.append('excludeId', excludeId);
    
    const response = await apiClient.get(`/api/v1/availability/check-conflicts?${queryParams.toString()}`);
    
    if (response.data && response.data.success) {
      return {
        hasConflicts: response.data.hasConflicts,
        conflicts: response.data.data
      };
    }
    
    return { hasConflicts: false, conflicts: [] };
  } catch (error) {
    console.error('Error checking conflicts:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la vérification des conflits');
  }
};

/**
 * Récupère les disponibilités d'un listing pour le calendrier
 * @param {string} listingId - ID du listing
 * @param {string} startDate - Date de début au format YYYY-MM-DD
 * @param {string} endDate - Date de fin au format YYYY-MM-DD
 * @returns {Promise<Object>} - Données de disponibilité
 */
export const getListingAvailability = async (listingId, startDate, endDate) => {
  try {
    console.log(`🔍 [AvailabilityService] Getting listing availability for: ${listingId} from ${startDate} to ${endDate}`);
    
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    
    const url = `/listings/${listingId}/availability?${queryParams.toString()}`;
    const response = await apiClient.get(url);
    
    console.log(`📊 [AvailabilityService] Availability response:`, response.data);
    
    if (response.data && response.data.success) {
      return {
        availabilities: response.data.data || [],
        totalCount: response.data.totalCount || 0
      };
    }
    
    return { availabilities: [], totalCount: 0 };
  } catch (error) {
    console.error('❌ [AvailabilityService] Error getting listing availability:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des disponibilités');
  }
};

// Export default avec toutes les méthodes
const availabilityService = {
  fetchAvailability,
  addAvailabilityPeriod,
  updateAvailabilityPeriod,
  deleteAvailabilityPeriod,
  deleteAvailabilityPeriodViaListing,
  blockPeriod,
  freePeriod,
  checkConflicts,
  getListingAvailability
};

export default availabilityService;
